import { functions } from '@/lib/firebase/config';
import { httpsCallable } from 'firebase/functions';

export type DonorContact = { displayName: string; email: string };

/**
 * Fetch donor display names + emails for a creator's own supporters.
 *
 * The dashboards used to read /users directly, one getDoc per donor, which is
 * the only reason the /users rule had to allow `get` to any signed-in account --
 * and Firestore has no field-level security, so that rule exposed the whole
 * document (email, postal address, stripeCustomerId) to anyone who knew a
 * userId. The server-side callable returns exactly the two fields these screens
 * use, and only for donors that actually belong to the caller.
 *
 * Deliberately no fallback to reading /users: a fallback would paper over a
 * broken callable right up until the rule closes, and then fail in production
 * instead of here. A missing id yields the same 'Anonymous' the old per-doc
 * read produced when a user document was absent.
 */
export async function fetchDonorContacts(userIds: string[]): Promise<Record<string, DonorContact>> {
    const unique = Array.from(new Set(userIds.filter(Boolean)));
    if (unique.length === 0) return {};

    // The callable caps a single request, so keep chunks under it.
    const CHUNK = 400;
    const callable = httpsCallable(functions as any, 'getDonorContacts');
    const out: Record<string, DonorContact> = {};

    for (let i = 0; i < unique.length; i += CHUNK) {
        const slice = unique.slice(i, i + CHUNK);
        try {
            const resp: any = await callable({ userIds: slice });
            const contacts = resp?.data?.contacts || {};
            for (const [id, c] of Object.entries(contacts as Record<string, any>)) {
                out[id] = {
                    displayName: c?.displayName || 'Anonymous',
                    email: c?.email || '',
                };
            }
        } catch (err) {
            console.error('[fetchDonorContacts] failed', err);
        }
    }
    return out;
}
